import { useCallback, useEffect, useRef, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { CreditCard, Loader2, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { PricingBreakdown } from '@/components/billing/PricingBreakdown';
import type { PricingDisplayContract } from '@/lib/billing/pricingDisplay';

type OwnerType = 'organization' | 'candidate';

interface AuthorizationQuote {
  supported: boolean;
  reason?: string | null;
  environment: string;
  plan: { id: string; name: string };
  billing_cycle: 'monthly' | 'yearly';
  renewal_pricing: PricingDisplayContract;
  quote_fingerprint: string;
  consent_version: string;
}

interface TokenizationConfig {
  attempt_id: string;
  status: string;
  authorization_active: boolean;
  resume_verification_url?: string | null;
  tokenization: {
    endpoint: string;
    publishable_api_key: string;
    callback_url: string;
    metadata: Record<string, string>;
  };
}

export function RecurringPaymentMethodDialog({
  client,
  ownerType,
  subscriptionId,
  onAuthorized,
}: {
  client: SupabaseClient;
  ownerType: OwnerType;
  subscriptionId: string;
  onAuthorized?: () => void | Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [quote, setQuote] = useState<AuthorizationQuote | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [consented, setConsented] = useState(false);
  const [card, setCard] = useState({ name: '', number: '', month: '', year: '', cvc: '' });
  const attemptKey = useRef<string | null>(null);
  const onAuthorizedRef = useRef(onAuthorized);

  useEffect(() => {
    onAuthorizedRef.current = onAuthorized;
  }, [onAuthorized]);

  const invoke = useCallback(async (body: Record<string, unknown>) => {
    const { data, error: invokeError } = await client.functions.invoke('recurring-payment-method', { body });
    if (invokeError) throw invokeError;
    if (data?.error || data?.reason && data?.supported === undefined) {
      throw new Error(String(data.reason ?? data.error));
    }
    return data;
  }, [client]);

  const loadQuote = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await invoke({ action: 'quote', owner_type: ownerType, subscription_id: subscriptionId });
      setQuote(data as AuthorizationQuote);
    } catch (quoteError) {
      setError(quoteError instanceof Error ? quoteError.message : 'authorization_quote_unavailable');
    } finally {
      setLoading(false);
    }
  }, [invoke, ownerType, subscriptionId]);

  useEffect(() => {
    if (open) void loadQuote();
  }, [loadQuote, open]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const attemptId = params.get('recurring_authorization_attempt_id');
    if (!attemptId) return;
    let cancelled = false;
    setOpen(true);
    setSubmitting(true);
    void invoke({
      action: 'finalize',
      owner_type: ownerType,
      subscription_id: subscriptionId,
      attempt_id: attemptId,
    }).then(async () => {
      if (cancelled) return;
      params.delete('recurring_authorization_attempt_id');
      window.history.replaceState({}, '', `${window.location.pathname}${params.size ? `?${params}` : ''}`);
      toast.success(t('recurringPaymentMethod.success'));
      await onAuthorizedRef.current?.();
      setOpen(false);
    }).catch((finalizeError) => {
      if (!cancelled) setError(finalizeError instanceof Error ? finalizeError.message : 'authorization_verification_failed');
    }).finally(() => {
      if (!cancelled) setSubmitting(false);
    });
    return () => { cancelled = true; };
  }, [invoke, ownerType, subscriptionId, t]);

  const submit = async () => {
    if (!quote?.supported || !consented || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      attemptKey.current ??= crypto.randomUUID();
      const begin = await invoke({
        action: 'begin',
        owner_type: ownerType,
        subscription_id: subscriptionId,
        idempotency_key: attemptKey.current,
        consent_accepted: true,
        consent_language: i18n.resolvedLanguage?.startsWith('ar') ? 'ar' : 'en',
        quote_fingerprint: quote.quote_fingerprint,
        return_url: `${window.location.origin}${window.location.pathname}`,
      }) as TokenizationConfig;

      if (begin.authorization_active) {
        attemptKey.current = null;
        toast.success(t('recurringPaymentMethod.success'));
        await onAuthorizedRef.current?.();
        setOpen(false);
        setSubmitting(false);
        return;
      }

      if (begin.resume_verification_url) {
        window.location.assign(begin.resume_verification_url);
        return;
      }

      // Sensitive card fields go directly from the browser to Moyasar. They are
      // never sent to a Fork Edge Function, database, log, or analytics path.
      const tokenResponse = await fetch(begin.tokenization.endpoint, {
        method: 'POST',
        headers: {
          Authorization: `Basic ${btoa(`${begin.tokenization.publishable_api_key}:`)}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: card.name,
          number: card.number.replace(/\s+/g, ''),
          month: Number(card.month),
          year: Number(card.year),
          cvc: card.cvc,
          callback_url: begin.tokenization.callback_url,
          metadata: begin.tokenization.metadata,
        }),
      });
      const token = await tokenResponse.json();
      if (!tokenResponse.ok || !token?.id) throw new Error(token?.message ?? 'provider_tokenization_failed');
      setCard({ name: '', number: '', month: '', year: '', cvc: '' });
      const bound = await invoke({
        action: 'bind',
        owner_type: ownerType,
        subscription_id: subscriptionId,
        attempt_id: begin.attempt_id,
        provider_token_id: token.id,
      });
      const verificationUrl = String(bound.verification_url ?? token.verification_url ?? '');
      if (!verificationUrl) throw new Error('provider_verification_url_missing');
      window.location.assign(verificationUrl);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'authorization_failed');
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !submitting && setOpen(next)}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" className="gap-2">
          <CreditCard className="h-4 w-4" />
          {t('recurringPaymentMethod.trigger')}
        </Button>
      </DialogTrigger>
      <DialogContent closeDisabled={submitting} className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('recurringPaymentMethod.title')}</DialogTitle>
          <DialogDescription>{t('recurringPaymentMethod.description')}</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> {t('recurringPaymentMethod.loading')}
          </div>
        ) : quote ? (
          <div className="space-y-4">
            <div className="rounded-lg border p-3 text-sm">
              <p className="font-semibold">{quote.plan.name}</p>
              <p className="text-muted-foreground">{t(`recurringPaymentMethod.${quote.billing_cycle}`)}</p>
            </div>
            <PricingBreakdown pricing={quote.renewal_pricing} />
            {!quote.supported ? (
              <p className="rounded-lg border border-amber-500/40 bg-amber-50 p-3 text-sm text-amber-900">
                {t('recurringPaymentMethod.unavailable')}
              </p>
            ) : (
              <>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="sm:col-span-2"><Label>{t('recurringPaymentMethod.cardholder')}</Label><Input autoComplete="cc-name" value={card.name} onChange={(e) => setCard((v) => ({ ...v, name: e.target.value }))} /></div>
                  <div className="sm:col-span-2"><Label>{t('recurringPaymentMethod.cardNumber')}</Label><Input inputMode="numeric" autoComplete="cc-number" value={card.number} onChange={(e) => setCard((v) => ({ ...v, number: e.target.value }))} /></div>
                  <div><Label>{t('recurringPaymentMethod.expiryMonth')}</Label><Input inputMode="numeric" autoComplete="cc-exp-month" value={card.month} onChange={(e) => setCard((v) => ({ ...v, month: e.target.value }))} /></div>
                  <div><Label>{t('recurringPaymentMethod.expiryYear')}</Label><Input inputMode="numeric" autoComplete="cc-exp-year" value={card.year} onChange={(e) => setCard((v) => ({ ...v, year: e.target.value }))} /></div>
                  <div><Label>{t('recurringPaymentMethod.cvc')}</Label><Input type="password" inputMode="numeric" autoComplete="cc-csc" value={card.cvc} onChange={(e) => setCard((v) => ({ ...v, cvc: e.target.value }))} /></div>
                </div>
                <div className="flex items-start gap-3 rounded-lg border p-3">
                  <Checkbox id="recurring-consent" checked={consented} onCheckedChange={(value) => setConsented(value === true)} />
                  <Label htmlFor="recurring-consent" className="font-normal leading-5">{t('recurringPaymentMethod.consent')}</Label>
                </div>
                <p className="flex items-start gap-2 text-xs text-muted-foreground"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />{t('recurringPaymentMethod.providerNotice')}</p>
              </>
            )}
          </div>
        ) : null}
        {error && <p className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{t('recurringPaymentMethod.error')}</p>}
        <DialogFooter>
          <Button type="button" variant="outline" disabled={submitting} onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
          <Button type="button" disabled={!quote?.supported || !consented || submitting || !card.name || !card.number || !card.month || !card.year || !card.cvc} onClick={submit}>
            {submitting && <Loader2 className="me-2 h-4 w-4 animate-spin" />}{t('recurringPaymentMethod.authorize')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
